import { useEffect, useState } from 'react';
import { Button, Form, Modal, Popconfirm, Space, message } from 'antd';
import 'moment-timezone'
import { TextService } from '../../Services/TextService';
import { useAuth } from '../../Contexts/AuthContext';
import { BookyInput } from '../../Components/Forms/BookyInput';
import { CompanyService } from '../../Services/CompanyService';
import { DomainState } from '../../Types/DomainState';
import { WebApiEmailDtoEmailDomainCreationDto } from '../../Repository/eventbookyapi';
import { CheckCircleFilled, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { BookyButton } from '../../Components/Buttons/BookyButton';

const EmailDomainForm = (props: any) => {
    const [domainState, setDomainState] = useState(DomainState.Unspecified as DomainState);
    const [domainResult, setDomainResult] = useState({} as WebApiEmailDtoEmailDomainCreationDto);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false as boolean);

    const [form] = Form.useForm();
    const { user } = useAuth();
    const GetText = TextService.GetText;
    const helpTrigger = <QuestionCircleOutlined style={{ color: "#1677ff", cursor: "pointer", fontSize: '20px' }} onClick={() => setIsHelpModalOpen(true)} />;
    const providerGuideLinks = [
        { text: "Loopia (CNAME)", url: "https://support.loopia.se/wiki/dnseditorn-a-och-cname/" },
        { text: "Loopia (TXT/DMARC)", url: "https://support.loopia.se/wiki/verifiera-doman-via-dns/" },
        { text: "One.com (CNAME)", url: "https://help.one.com/hc/sv/articles/360000803517-Hur-skapar-jag-en-CNAME-post-" },
        { text: "One.com (TXT)", url: "https://help.one.com/hc/sv/articles/360000825478-Hur-skapar-jag-en-TXT-post" },
        { text: "Wix (DKIM/TXT)", url: "https://support.wix.com/en/article/adding-or-updating-txt-records-in-your-wix-account" },
    ];

    const CreateDomainClick = async (values: any) => {
        try {
            const result = await CompanyService.AddEmailDomain();
            props.loadCompanySettings();
            message.success(GetText("Common_SettingsUpdated"));
        } catch {
            message.warning(GetText("EmailDomainForm_Error"));
        }
    };

    const DeleteDomainClick = async (values: any) => {
        await CompanyService.DeleteEmailDomain();
        message.success(GetText("Common_SettingsUpdated"));
        props.loadCompanySettings();
    };

    const AuthenticateDomainClick = async (values: any) => {
        const result = await CompanyService.AuthenticateDomain();
        message.success(result.message);
    };

    useEffect(() => {
        if (props != null && props.company != null && props.company.email != null) {
            if (props?.company?.domain?.length > 0) {
                setDomainState(DomainState.Created);
                form.setFieldValue("domain", props.company.domain);
                const domainStatus = CompanyService.GetEmailDomain().then(result => {
                    if (result.dnsRecords?.brevoCode?.status === true) {
                        setDomainState(DomainState.Authenticated);
                    }
                    setDomainResult(result);
                });
            }
            else {
                setDomainState(DomainState.NotCreated);
                form.setFieldValue("domain", props.company.email.split("@")[1]);
            }
        }

    }, [props]);

    return (
        <>
            {domainState == DomainState.NotCreated ? <Form
                colon={false}
                form={form}
                labelCol={{
                    span: 3,
                }}
                onFinish={CreateDomainClick}
                wrapperCol={{
                    span: 6,
                }}
                layout="vertical"
            >
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: "10px" }}>
                    <span>{GetText("EmailDomainForm_Description")}</span>
                    {helpTrigger}
                </Space>
                <BookyInput label={"Domän"} disabled={true} name="domain" req></BookyInput>
                <Form.Item label={" "}>
                    <Button data-testid="submit-button" style={{ float: 'left' }} size="middle" type="primary" htmlType="submit">
                        {GetText("EmailDomainForm_AddDomainButton")}
                    </Button>
                </Form.Item>
            </Form> : ""}

            {domainState == DomainState.Created || domainState == DomainState.Authenticated ? <Form
                colon={false}
                form={form}
                labelCol={{
                    span: 3,
                }}
                wrapperCol={{
                    span: 6,
                }}
                layout="vertical"
            >
                <Space style={{ width: '100%', justifyContent: 'space-between', margin: 0, marginBottom: "10px" }}>
                    <span>{domainState == DomainState.Authenticated ? GetText("EmailDomainForm_DomainIsAuthenticated") : GetText("EmailDomainForm_DomainCreatedDescription")}</span>
                    {helpTrigger}
                </Space>
                {domainResult.dnsRecords?.brevoCode?.status ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleOutlined />} <b>Brevo code</b> <br />
                type: {domainResult.dnsRecords?.brevoCode?.type} <br />
                name: @<br />
                value: <span style={{ backgroundColor: "#e4e4e4", overflowWrap: "break-word" }}>{domainResult.dnsRecords?.brevoCode?.value}</span> <br />

                {domainResult.dnsRecords?.dkim1Record?.status ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleOutlined />} <b>DKIM 1 record</b> <br />
                type: {domainResult.dnsRecords?.dkim1Record?.type} <br />
                name: {domainResult.dnsRecords?.dkim1Record?.hostName}<br />
                value: <span style={{ backgroundColor: "#e4e4e4", overflowWrap: "break-word" }}>{domainResult.dnsRecords?.dkim1Record?.value}</span> <br />

                {domainResult.dnsRecords?.dkim2Record?.status ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleOutlined />} <b>DKIM 2 record</b> <br />
                type: {domainResult.dnsRecords?.dkim2Record?.type} <br />
                name: {domainResult.dnsRecords?.dkim2Record?.hostName}<br />
                value: <span style={{ backgroundColor: "#e4e4e4", overflowWrap: "break-word" }}>{domainResult.dnsRecords?.dkim2Record?.value}</span> <br />

                {domainResult.dnsRecords?.dmarcRecord?.status ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleOutlined />} <b>Dmarc record</b> <br />
                type: {domainResult.dnsRecords?.dmarcRecord?.type} <br />
                name: {domainResult.dnsRecords?.dmarcRecord?.hostName}<br />
                value: <span style={{ backgroundColor: "#e4e4e4", overflowWrap: "break-word" }}>{domainResult.dnsRecords?.dmarcRecord?.value}</span> <br />
                <BookyInput label={"Domän"} disabled={true} name="domain" req></BookyInput>
                <Form.Item label={" "}>
                    <Space>
                        <BookyButton type={"primary"} onClick={AuthenticateDomainClick} disabled={domainResult.dnsRecords?.brevoCode?.status && domainResult.dnsRecords?.dkim1Record?.status} htmlType="submit" text={GetText("EmailDomainForm_AuthDomain")}></BookyButton>
                    </Space>
                </Form.Item>
            </Form > : ""
            }

            <Modal
                open={isHelpModalOpen}
                title={GetText("EmailDomainForm_HelpModalTitle")}
                onCancel={() => setIsHelpModalOpen(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setIsHelpModalOpen(false)}>
                        {GetText("EmailDomainForm_HelpModalClose")}
                    </Button>
                ]}
            >
                <p>{GetText("EmailDomainForm_HelpModalIntro")}</p>
                <p><b>{GetText("EmailDomainForm_HelpModalWhyTitle")}</b></p>
                <p>{GetText("EmailDomainForm_HelpModalWhyText")}</p>

                <p><b>{GetText("EmailDomainForm_HelpModalStep1Title")}</b></p>
                <ol>
                    <li>{GetText("EmailDomainForm_HelpModalStep1Item1")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep1Item2")} <a href="https://internetstiftelsen.se/sok-doman/" target="_blank" rel="noreferrer">internetstiftelsen.se/sok-doman</a></li>
                    <li>{GetText("EmailDomainForm_HelpModalStep1Item3")} <a href="https://www.who.is" target="_blank" rel="noreferrer">who.is</a></li>
                </ol>

                <p><b>{GetText("EmailDomainForm_HelpModalStep2Title")}</b></p>
                <ol>
                    <li>{GetText("EmailDomainForm_HelpModalStep2Item1")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep2Item2")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep2Item3")}</li>
                </ol>

                <p><b>{GetText("EmailDomainForm_HelpModalStep3Title")}</b></p>
                <ol>
                    <li>{GetText("EmailDomainForm_HelpModalStep3Item1")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep3Item2")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep3Item3")}</li>
                </ol>

                <p><b>{GetText("EmailDomainForm_HelpModalStep4Title")}</b></p>
                <ol>
                    <li>{GetText("EmailDomainForm_HelpModalStep4Item1")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep4Item2")}</li>
                    <li>{GetText("EmailDomainForm_HelpModalStep4Item3")}</li>
                </ol>

                <p><b>{GetText("EmailDomainForm_HelpModalGuidesTitle")}</b></p>
                <ul>
                    {providerGuideLinks.map(link => <li key={link.url}><a href={link.url} target="_blank" rel="noreferrer">{link.text}</a></li>)}
                </ul>

                <p>{GetText("EmailDomainForm_HelpModalDnsInfo")}</p>
            </Modal>

        </>

    )

};

export default EmailDomainForm;